import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { brandActions } from "../slices/brand";
import { getErrorMessage } from "../../utils/errorUtils";
import { BrandRequestDto } from "../../shared/dtos/brand/BrandRequestDto";
import { BrandResponseDto } from "../../shared/dtos/brand/BrandResponseDto";
import { UpdateBrandPayload } from "../../shared/interfaces/brand/UpdateBrandPayload";
import { PaginationQueryDto } from "../../shared/dtos/common/PaginationQueryDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const getBrands =
  (params?: PaginationQueryDto) => async (dispatch: AppDispatch) => {
    dispatch(brandActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<BrandResponseDto>>
      >("/brands", { headers: { requiresAuth: true }, params });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(brandActions.setItems(data.data.content));
        dispatch(brandActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(brandActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getBrandById = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(brandActions.setLoadingFetchOne());
  try {
    const { data } = await axiosInstance.get<ApiResponseDto<BrandResponseDto>>(
      `/brands/${id}`,
      { headers: { requiresAuth: true } },
    );
    if (data.status === ApiResponseStatus.SUCCESS && data.data) {
      dispatch(brandActions.setItem(data.data));
    }
  } catch (error) {
    dispatch(brandActions.setErrorFetchOne(getErrorMessage(error)));
  }
};

export const createBrand =
  (values: BrandRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(brandActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<BrandResponseDto>
      >("/brands", values, { headers: { requiresAuth: true } });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Brand created!");
        dispatch(brandActions.createSuccess(data.data));
        dispatch(
          brandActions.setServerStatus({ operation: "create", status: 201 }),
        );
      }
    } catch (error) {
      dispatch(brandActions.setErrorCreate(getErrorMessage(error)));
    }
  };

export const updateBrand =
  (payload: UpdateBrandPayload) => async (dispatch: AppDispatch) => {
    dispatch(brandActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<BrandResponseDto>
      >(`/brands/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Brand updated!");
        dispatch(brandActions.updateSuccess(data.data));
      }
    } catch (error) {
      dispatch(brandActions.setErrorUpdate(getErrorMessage(error)));
    }
  };

export const deleteBrand = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(brandActions.setLoadingDelete());
  try {
    const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
      `/brands/${id}`,
      { headers: { requiresAuth: true } },
    );
    if (data.status === ApiResponseStatus.SUCCESS) {
      toast.success(data.message || "Brand deleted.");
      dispatch(brandActions.deleteSuccess(id));
    }
  } catch (error) {
    dispatch(brandActions.setErrorDelete(getErrorMessage(error)));
  }
};

export const resetBrandServerStatus = () => (dispatch: AppDispatch) =>
  dispatch(brandActions.setServerStatus(null));
